// pages/sop/SopViewer.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import LinkIcon from "@mui/icons-material/Link";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

// ─── Design tokens (matched to FoldersList) ─────────────────────────────────
const TEAL = "#0c8a9b";
const TEAL_DARK = "#0a7a8a";
const TEAL_LIGHT = "#e6f6f8";
const FONT = "'DM Sans', 'Inter', sans-serif";

const KF_ID = "sop-viewer-kf";
if (typeof document !== "undefined" && !document.getElementById(KF_ID)) {
  const s = document.createElement("style");
  s.id = KF_ID;
  s.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
    @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  `;
  document.head.appendChild(s);
}

const statusStyles = {
  DRAFT: { bg: "#f1f3f5", color: "#64748b" },
  PUBLISHED: { bg: TEAL_LIGHT, color: TEAL },
  ARCHIVED: { bg: "#fef3e2", color: "#e07b00" },
};

const SectionCard = ({ title, children, delay = "0s" }) => (
  <Card
    sx={{
      borderRadius: "16px",
      border: "1px solid #edf0f2",
      boxShadow: "0 1px 2px rgba(16,24,40,.04)",
      mb: 2.5,
      animation: `fadeUp .45s ease ${delay} both`,
    }}
  >
    <CardContent sx={{ p: 2.8 }}>
      <Typography
        sx={{
          fontFamily: FONT,
          fontWeight: 700,
          fontSize: "0.95rem",
          color: "#111827",
          mb: 1.8,
        }}
      >
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

const SopViewer = () => {
  const { sopId } = useParams();
  const navigate = useNavigate();
  const [sop, setSop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/sop-manager/sops/${sopId}`)
      .then((res) => setSop(res.data.data))
      .catch(() => setError("Failed to load SOP"))
      .finally(() => setLoading(false));
  }, [sopId]);

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress sx={{ color: TEAL }} />
      </Box>
    );

  if (error)
    return (
      <Box sx={{ maxWidth: 1000, mx: "auto", p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: "12px", fontFamily: FONT }}>
          {error}
        </Alert>
      </Box>
    );

  if (!sop) return null;

  return (
    <Box sx={{ minHeight: "100vh", background: "#f8f9fa", fontFamily: FONT }}>
      {/* ── Sticky header: back button + title + edit action always reachable ── */}
      <Box
        sx={{
          zIndex: 20,
          background: "rgba(248,249,250,.92)",
          backdropFilter: "blur(8px)",
          borderBottom: "1px solid #edf0f2",
          px: { xs: 2, sm: 3.5 },
          py: 1.6,
        }}
      >
        <Box
          sx={{
            maxWidth: 1000,
            mx: "auto",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Tooltip title="Back">
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                bgcolor: "#fff",
                border: "1px solid #edf0f2",
                borderRadius: "10px",
                "&:hover": { borderColor: TEAL, color: TEAL },
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: "1.05rem",
                color: "#1a1d23",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {sop.title}
            </Typography>
            <Typography
              sx={{ fontFamily: FONT, fontSize: "0.75rem", color: "#868e96" }}
            >
              Standard Operating Procedure
            </Typography>
          </Box>

          <Chip
            label={sop.status}
            size="small"
            sx={{
              bgcolor: statusStyles[sop.status]?.bg || "#f1f3f5",
              color: statusStyles[sop.status]?.color || "#64748b",
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: "0.7rem",
            }}
          />

          <Button
            variant="contained"
            startIcon={
              <EditOutlinedIcon sx={{ fontSize: "16px !important" }} />
            }
            onClick={() => navigate(`/ats/sop/edit/${sopId}`)}
            sx={{
              background: TEAL,
              borderRadius: "10px",
              px: 2,
              fontFamily: FONT,
              fontWeight: 600,
              color: "#fff",
              fontSize: "0.82rem",
              textTransform: "none",
              boxShadow: "none",
              whiteSpace: "nowrap",
              "&:hover": { background: TEAL_DARK, boxShadow: "none" },
            }}
          >
            Edit
          </Button>
        </Box>
      </Box>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <Box sx={{ maxWidth: 1000, mx: "auto", p: { xs: 2, sm: 3.5 } }}>
        {sop.description && (
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: "0.92rem",
              color: "#6b7280",
              mb: 2.5,
              animation: "fadeUp .4s ease both",
            }}
          >
            {sop.description}
          </Typography>
        )}

        {sop.file_attachments && sop.file_attachments.length > 0 && (
          <SectionCard title="File Attachments" delay="0s">
            <Grid container spacing={1.2}>
              {sop.file_attachments.map((file, idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <Box
                    component="a"
                    href={file.url}
                    download={file.name}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      p: 1.2,
                      borderRadius: "10px",
                      border: "1px solid #edf0f2",
                      bgcolor: "#fafbfc",
                      textDecoration: "none",
                      transition: "all .15s ease",
                      "&:hover": {
                        borderColor: TEAL,
                        bgcolor: TEAL_LIGHT,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        bgcolor: TEAL_LIGHT,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <InsertDriveFileOutlinedIcon
                        sx={{ fontSize: 17, color: TEAL }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        color: "#1a1d23",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {file.name}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        )}

        {sop.link_attachments && sop.link_attachments.length > 0 && (
          <SectionCard title="Links" delay=".05s">
            <Grid container spacing={1.2}>
              {sop.link_attachments.map((link, idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <Box
                    component="a"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      p: 1.2,
                      borderRadius: "10px",
                      border: "1px solid #edf0f2",
                      bgcolor: "#fafbfc",
                      textDecoration: "none",
                      transition: "all .15s ease",
                      "&:hover": {
                        borderColor: "#3b5bdb",
                        bgcolor: "#edf2ff",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "8px",
                        bgcolor: "#edf2ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <LinkIcon sx={{ fontSize: 17, color: "#3b5bdb" }} />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: FONT,
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        color: "#1a1d23",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {link.name}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </SectionCard>
        )}

        <SectionCard title="Content" delay=".1s">
          <Box
            sx={{
              fontFamily: FONT,
              fontSize: "0.92rem",
              lineHeight: 1.75,
              color: "#1f2937",
              "& img": { maxWidth: "100%", borderRadius: "10px" },
              "& a": { color: TEAL },
              "& h1, & h2, & h3": { fontWeight: 700, color: "#111827" },
            }}
            dangerouslySetInnerHTML={{ __html: sop.content }}
          />
        </SectionCard>
      </Box>
    </Box>
  );
};

export default SopViewer;
